import api from "./api";
import { ok, getMe, can, reasonOf, utcToday, ORG_SCOPES } from "./core";
import { invalidateEmployees } from "./employeeApi";

export async function deactivateEmployee(employeeId, { status = "Inactive", exitDate, replacementManagerId, reason } = {}) {
  const me = await getMe();
  if (employeeId === me.employee_id) throw new Error("You cannot deactivate yourself.");
  const apiStatus = String(status).toUpperCase() === "EXITED" ? "EXITED" : "INACTIVE";
  const { data } = await api.post(`/employees/${employeeId}/deactivation`, {
    employment_status: apiStatus,
    exit_date: apiStatus === "EXITED" ? exitDate || utcToday() : null,
    replacement_manager_id: replacementManagerId || null,
    reason: reasonOf(reason, apiStatus === "EXITED" ? "Employee exited" : "Employee deactivated"),
  });
  invalidateEmployees();
  return ok(data);
}

export function describeDeactivation(name, summary) {
  if (!summary) return `${name} was deactivated.`;
  const parts = [];
  if (summary.account_disabled) parts.push("login disabled");
  if (summary.records_excluded) parts.push(`${summary.records_excluded} open appraisal${summary.records_excluded === 1 ? "" : "s"} excluded`);
  if (summary.proposals_withdrawn) parts.push(`${summary.proposals_withdrawn} KRA/KPI proposal${summary.proposals_withdrawn === 1 ? "" : "s"} withdrawn`);
  if (summary.invitations_cancelled) parts.push("pending login invitation cancelled");
  return `${name} is now ${summary.employment_status === "EXITED" ? "exited" : "inactive"}${parts.length ? `: ${parts.join(", ")}` : ""}. History is kept.`;
}

export async function canManageLogins() {
  return can(await getMe(), "identity.manage", ["SYSTEM_ALL"]);
}

// [NEW] DELETE /employees/{employee_id} - permanently removes an employee who has no linked records
// (no login, roles, reporting lines, goals, appraisals or history). The server requires a reason and the
// employee.manage permission, and answers 409 RECORD_IN_USE / SELF_DELETE_FORBIDDEN when it cannot delete.
export async function deleteEmployee(employeeId, reason) {
  const me = await getMe();
  if (employeeId === me.employee_id) throw new Error("You cannot delete your own employee record.");
  const text = String(reason ?? "").trim();
  if (!text) throw new Error("Please give a reason for deleting this employee.");
  const { data } = await api.delete(`/employees/${employeeId}`, { data: { reason: reasonOf(text) } });
  invalidateEmployees();
  return ok(data);
}

export async function canDeleteEmployee() {
  return can(await getMe(), "employee.manage", ORG_SCOPES);
}
